import { Link } from 'react-router';
import { ArrowRight, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { MyPlan } from '@/services/planShopping';
import { formatLongDate, SHOP_FOR_PLAN_PATH } from '@/lib/planShopping';

const startsIn = (days: number) =>
    days <= 0 ? 'Starts today' : days === 1 ? 'Starts tomorrow' : `Starts in ${days} days`;

/**
 * The white "Shop for your plan" panel that sits inside My Menu's blue header.
 */
export function ShopForPlanPanel({ plan }: { plan: MyPlan }) {
    const upcoming = plan.phase === 'UPCOMING';
    return (
        <div className="mt-5 flex flex-col gap-4 rounded-2xl bg-card p-4 text-foreground sm:flex-row sm:items-center sm:p-5">
            <div className="flex flex-1 items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-fill-lime/40">
                    <ShoppingCart className="h-5 w-5 text-brand-text-green" />
                </div>
                <div className="min-w-0">
                    <h2 className="font-bold leading-snug">
                        {upcoming ? 'Get your shopping done before day one' : 'Shop for your plan'}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Pick the days and meals you want to shop for. Any range, any meals.
                    </p>
                </div>
            </div>
            <Button asChild size="lg" className="h-11 shrink-0 text-base">
                <Link to={SHOP_FOR_PLAN_PATH}>
                    <ShoppingCart className="h-5 w-5" />
                    Shop for your plan
                </Link>
            </Button>
        </div>
    );
}

/** "Starts in N days" pill for an upcoming plan. */
export function StartsInBadge({ days }: { days: number }) {
    return (
        <span className="inline-flex items-center rounded-full bg-brand-fill-yellow px-3 py-1 text-sm font-semibold text-brand-fill-ink">
            {startsIn(days)}
        </span>
    );
}

/** The dashboard card: plan name, when it starts, and the way into shopping. */
export function ShopForPlanCard({ plan }: { plan: MyPlan }) {
    const upcoming = plan.phase === 'UPCOMING';
    return (
        <div className="mb-8 rounded-2xl bg-brand-header p-6 text-brand-header-ink">
            <div className="flex items-start gap-4">
                <div className="min-w-0 flex-1">
                    <p className="text-sm">
                        {upcoming
                            ? `Your plan starts ${formatLongDate(plan.startDate)}`
                            : plan.phase === 'IN_PROGRESS'
                              ? `Day ${plan.currentDayNumber} of ${plan.duration}`
                              : 'Your plan'}
                    </p>
                    <h2 className="text-2xl font-bold">{plan.program.name}</h2>
                    <p className="text-sm">
                        {plan.duration} days
                        {upcoming ? ` · ${startsIn(plan.daysUntilStart).toLowerCase()}` : ''}
                    </p>
                </div>
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-black/15">
                    <ShoppingCart className="h-6 w-6" />
                </div>
            </div>
            <p className="mt-4 max-w-2xl">
                {upcoming
                    ? "Get your shopping done before day one. Choose the days and meals, and we'll make the list."
                    : "Choose the days and meals you want to shop for, and we'll make the list."}
            </p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <Link
                    to={SHOP_FOR_PLAN_PATH}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-card px-6 font-semibold text-brand-text-blue hover:bg-brand-header-wash"
                >
                    <ShoppingCart className="h-5 w-5" />
                    Shop for your plan
                </Link>
                <Link
                    to="/my-menu"
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-brand-header-ink/60 px-6 font-medium hover:bg-black/10"
                >
                    See your menu
                    <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </div>
    );
}
